/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @next/next/no-img-element */
import React, {
  useRef,
  ImgHTMLAttributes,
  useLayoutEffect,
  useEffect,
  useMemo,
} from 'react';
import { handleImageUrl } from '$/utils/image';
import { useIntersection } from './useIntersection';
import { useImagePreload, addGlobalPreload } from './ImagePreloadContext';

const VALID_LOADING_VALUES = ['lazy', 'eager', undefined] as const;

type LoadingValue = typeof VALID_LOADING_VALUES[number];

interface LazyImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: SrcType;
  alt: string;
  showWatermark?: boolean;
  placeholder?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  lazyRoot?: React.RefObject<HTMLElement> | null;
  lazyBoundary?: string;
  className?: string;
  priority?: boolean;
  /**
   * 图片节点需要加载原图，只追加格式/水印处理，不向图片云服务传宽度。
   */
  useOriginalImage?: boolean;
  /**
   * 原图模式下保留源文件编码；无水印时直接使用源 URL，避免透明通道被图片云格式化。
   */
  preserveSourceFormat?: boolean;
  /**
   * 响应式 sizes 配置，支持类似 Next.js Image 的格式
   * @example "100vw" - 全宽
   * @example "50vw" - 半宽
   * @example "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
   * @example "300px" - 固定宽度
   */
  sizes?: string;
  loading?: LoadingValue;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  onError?: () => void;
  // 其他你想支持的 img 属性可以单独加
}
type SrcType =
  | string
  | { src: string; width?: number; height?: number; };

// 默认的设备宽度断点，用于生成 srcset（与 Next.js 默认值一致）
const DEFAULT_DEVICE_SIZES = [640, 750, 828, 1080, 1200, 1920, 2048, 3840];
// 用于固定宽度图片的尺寸（与 Next.js 默认值一致）
const DEFAULT_IMAGE_SIZES = [16, 32, 48, 64, 96, 128, 256, 384];

/**
 * 获取原始图片 URL
 */
function getBaseUrl(url: SrcType): string {
  if (!url) return '';
  if (typeof url !== 'string') {
    return url.src;
  }
  return url;
}

/**
 * 检查 URL 是否可以优化
 */
function canOptimize(url: string): boolean {
  if (!url) return false;
  // SVG 不需要优化
  if (url.endsWith('.svg')) return false;
  return /^http/.test(url) && !url.includes('imageView2');
}

/**
 * 获取缩略图
 * @param  {String} url   原始URL，七牛云图片地址
 * @param  {String} width   宽度
 * @returns {string} 返回图片URL的缩略图
 */
export function getThumbnailUrl(url: SrcType, width: number, showWatermark = false): string {
  const baseUrl = getBaseUrl(url);
  if (!baseUrl || !canOptimize(baseUrl)) return baseUrl;

  return handleImageUrl(baseUrl, { size: width, showWatermark });
}

/**
 * 解析 sizes 属性，提取最大宽度值
 * 例如: "(max-width: 768px) 100vw, 300px" -> 提取出 300（固定像素值）
 * 例如: "100vw" -> 返回 undefined（响应式）
 */
function parseSizesWidth(sizes: string): number | undefined {
  // 匹配固定像素值，如 "300px"、"(max-width: 768px) 100vw, 300px"
  // 取最后一个非媒体查询的值
  const parts = sizes.split(',').map((s) => s.trim());
  const lastPart = parts[parts.length - 1];

  // 检查是否是固定像素值（不在媒体查询中）
  if (lastPart && !lastPart.includes('(') && lastPart.includes('px')) {
    const match = lastPart.match(/(\d+)px/);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  return undefined;
}

/**
 * 生成 srcset 的宽度列表
 * 与 Next.js 逻辑一致：
 * - 如果 sizes 是固定像素值（如 "300px"），使用 imageSizes + deviceSizes 并过滤
 * - 如果 sizes 是响应式（如 "100vw"），只使用 deviceSizes
 */
function getWidthsFromSizes(sizes: string, imgWidth: number | undefined): number[] {
  const fixedWidth = parseSizesWidth(sizes);

  if (fixedWidth) {
    // 固定像素值：使用 imageSizes + deviceSizes，只保留 <= fixedWidth * 2 的尺寸
    const allSizes = [...DEFAULT_IMAGE_SIZES, ...DEFAULT_DEVICE_SIZES];
    const maxWidth = fixedWidth * 2; // 考虑 2x 屏幕
    return allSizes.filter((w) => w <= maxWidth);
  }

  // 响应式布局（vw、%）：使用所有 deviceSizes
  return DEFAULT_DEVICE_SIZES;
}

/**
 * 生成 srcset 属性
 *
 * Next.js Image 的 srcset 策略：
 * 1. 有 sizes 属性: 使用 w 描述符
 *    - sizes="100vw" → 生成所有 deviceSizes
 *    - sizes="300px" → 生成 imageSizes + deviceSizes 中 <= 600 的尺寸
 *    - 浏览器根据 sizes + DPR 自动选择最合适的图片
 *
 * 2. 没有 sizes 属性: 使用 x 描述符，只生成 1x 和 2x
 *    - 浏览器根据设备像素比选择
 *
 * @param url 原始图片 URL
 * @param sizes sizes 属性值
 * @param imgWidth 图片的 width 属性，固定尺寸时使用
 * @param showWatermark 是否显示水印
 * @returns srcset 字符串
 */
function generateSrcSet(
  url: SrcType,
  sizes: string | undefined,
  imgWidth: number | string | undefined,
  showWatermark: boolean,
): string {
  const baseUrl = getBaseUrl(url);
  if (!baseUrl || !canOptimize(baseUrl)) return '';

  const width = typeof imgWidth === 'string' ? parseInt(imgWidth, 10) : imgWidth;

  // 有 sizes 属性：使用 w 描述符
  if (sizes) {
    const widths = getWidthsFromSizes(sizes, width);
    return widths
      .map((w) => {
        const optimizedUrl = handleImageUrl(baseUrl, { size: w, showWatermark });
        return `${optimizedUrl} ${w}w`;
      })
      .join(', ');
  }

  // 没有 sizes 属性：使用 x 描述符 (1x, 2x)
  // 默认使用 1080 作为基准宽度
  const baseWidth = width && width > 0 ? width : 1080;

  // 生成 1x 和 2x 版本
  const srcsetWidths = [baseWidth, baseWidth * 2];
  return srcsetWidths
    .map((w, index) => {
      const optimizedUrl = handleImageUrl(baseUrl, { size: w, showWatermark });
      return `${optimizedUrl} ${index + 1}x`;
    })
    .join(', ');
}

/**
 * 统一图片渲染组件，默认走缩略图优化，也支持特定场景加载原图格式化地址。
 */
function LazyImage({
  src,
  showWatermark = false,
  alt,
  placeholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAA+gAAAABAQMAAAB3zhZwAAAAA1BMVEUAAACnej3aAAAAAXRSTlMAQObYZgAAAAtJREFUCNdjGFAAAAB+AAGrKkCzAAAAAElFTkSuQmCC', // 图片占位图待替换
  width,
  height,
  style,
  sizes,
  className,
  lazyRoot = null,
  lazyBoundary = '300px',
  loading,
  priority = false,
  useOriginalImage = false,
  preserveSourceFormat = false,
  onLoad,
  onError,
  ...restProps
}: LazyImageProps) {
  // 开发环境警告：必须传递 width 和 sizes 属性
  // if (process.env.NODE_ENV === 'development') {
  //   const srcUrl = typeof src === 'string' ? src : src?.src;

  //   if (!width) {
  //     // eslint-disable-next-line no-console
  //     console.warn(
  //       `${+'请为图片设置 width 属性以获得最佳性能。'
  //       }\n图片 src: ${srcUrl}`,
  //     );
  //   }
  //   if (!sizes) {
  //     // eslint-disable-next-line no-console
  //     console.warn(
  //       `${+'请设置 sizes 属性（如 "100vw" 或 "(max-width: 768px) 100vw, 50vw"）以启用响应式图片优化。'
  //       }\n图片 src: ${srcUrl}`,
  //     );
  //   }
  // }

  const imgRef = useRef<HTMLImageElement>(null);
  const isLazy = !priority && (loading === 'lazy' || typeof loading === 'undefined');
  // 在服务端使用 useEffect，在客户端使用 useLayoutEffect
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  const [setIntersection, isIntersected] = useIntersection<HTMLImageElement>({
    rootRef: lazyRoot,
    rootMargin: lazyBoundary,
    disabled: !isLazy,
  });
  const isVisible = !isLazy || isIntersected;
  const imgWidth = useOriginalImage ? undefined : width;
  const imgSizes = useOriginalImage ? undefined : sizes;

  // 默认 src 使用中等尺寸，原图模式按需保留源格式或仅追加格式/水印处理。
  const defaultSrc = useMemo(() => {
    if (!isVisible) return placeholder;
    const baseUrl = getBaseUrl(src);
    if (!baseUrl || !canOptimize(baseUrl)) return baseUrl;
    if (useOriginalImage) {
      if (preserveSourceFormat && !showWatermark) return baseUrl;
      return handleImageUrl(baseUrl, {
        showWatermark,
        needFormat: !preserveSourceFormat,
      });
    }
    // 使用 1080 作为默认 fallback 宽度
    return handleImageUrl(baseUrl, { size: 640, showWatermark });
  }, [src, showWatermark, isVisible, placeholder, preserveSourceFormat, useOriginalImage]);

  // ============================================
  // Preload 支持：priority 图片在 SSR 时注入 <link rel="preload">
  // ============================================
  const preloadContext = useImagePreload();

  // 计算 preload 需要的值（priority 时才计算）
  const preloadSrcSet = useMemo(() => {
    if (!priority) return '';
    if (useOriginalImage) return '';
    return generateSrcSet(src, imgSizes, imgWidth, showWatermark);
  }, [priority, src, imgSizes, imgWidth, showWatermark, useOriginalImage]);

  const preloadHref = useMemo(() => {
    if (!priority) return '';
    const baseUrl = getBaseUrl(src);
    if (!baseUrl || !canOptimize(baseUrl)) return baseUrl;
    if (useOriginalImage) {
      if (preserveSourceFormat && !showWatermark) return baseUrl;
      return handleImageUrl(baseUrl, {
        showWatermark,
        needFormat: !preserveSourceFormat,
      });
    }
    return handleImageUrl(baseUrl, { size: 1080, showWatermark });
  }, [preserveSourceFormat, priority, src, showWatermark, useOriginalImage]);

  // SSR 时注册 preload（在渲染阶段同步执行，不在 effect 中）
  if (priority && typeof window === 'undefined' && preloadHref) {
    const preloadInfo = {
      href: preloadHref,
      srcSet: preloadSrcSet || undefined,
      sizes: imgSizes || undefined,
    };

    // 优先使用 Context，回退到全局收集器
    if (preloadContext) {
      preloadContext.addPreload(preloadInfo);
    } else {
      addGlobalPreload(preloadInfo);
    }
  }

  // 客户端：对于 priority 图片，动态注入 preload link（作为降级方案）
  useEffect(() => {
    if (!priority || typeof document === 'undefined' || !preloadHref) return;

    // 检查是否已存在相同的 preload link（SSR 可能已经注入）
    const existingLink = document.querySelector(`link[rel="preload"][href="${preloadHref}"]`);
    if (existingLink) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = preloadHref;
    if (preloadSrcSet) link.setAttribute('imagesrcset', preloadSrcSet);
    if (imgSizes) link.setAttribute('imagesizes', imgSizes);

    document.head.appendChild(link);

    // 组件卸载时不移除 link，因为图片可能已经被缓存
    // 且移除可能影响其他使用相同图片的组件
  }, [priority, preloadHref, preloadSrcSet, imgSizes]);

  // 包装 onError 回调，只在真实图片加载失败时触发
  const handleError = () => {
    // 只有当当前 src 不是 placeholder 时才触发回调
    if (defaultSrc !== placeholder && onError) {
      onError();
    }
  };

  useIsomorphicLayoutEffect(() => {
    setIntersection(imgRef.current);
  }, [setIntersection]);

  // 只在真实图片加载完成时触发 onLoad，避免 placeholder 触发
  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (isVisible && onLoad) {
      onLoad(e);
    }
  };

  return (
    <img
      ref={imgRef}
      src={defaultSrc}
      sizes={imgSizes}
      alt={alt}
      width={width}
      height={height}
      style={{
        height,
        ...style,
      }}
      className={className}
      loading={loading}
      onLoad={handleLoad}
      onError={handleError}
      crossOrigin="anonymous"
      {...restProps}
    />
  );
}

// 导出 preload 相关 API
export {
  ImagePreloadProvider,
  ImagePreloadHead,
  useImagePreload,
  addGlobalPreload,
  getGlobalPreloads,
  resetGlobalPreloads,
  generatePreloadLinksHTML,
} from './ImagePreloadContext';

export type { PreloadImageInfo } from './ImagePreloadContext';

export default LazyImage;
